import { Link } from "react-router-dom";
import { Users, Globe, Mail, AlertTriangle, Receipt, Boxes, Tag, TrendingUp } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminOverview } from "@/hooks/useAdmin";
import { cn } from "@/lib/utils";

export function AdminOverviewPage() {
  const { data, isLoading } = useAdminOverview();

  return (
    <div className="space-y-5 sm:space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Overview</h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">
          Platform-wide counts. Refreshes every 30 seconds.
        </p>
      </div>

      {isLoading || !data ? (
        <Skeletons />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <Tile to="/admin/customers" icon={<Users className="h-5 w-5" />}      label="Customers"        value={data.customers_total}      hint={`+${data.customers_last_7d} last 7 days`} />
          <Tile to="/admin/customers" icon={<TrendingUp className="h-5 w-5" />} label="Active subs"      value={data.active_subscriptions} />
          <Tile to="/admin/customers" icon={<Globe className="h-5 w-5" />}      label="Domains"          value={data.domains_total} />
          <Tile to="/admin/leads"     icon={<Mail className="h-5 w-5" />}       label="Open leads"       value={data.leads_open}           accent={data.leads_open > 0 ? "warn" : "default"} />
          <Tile to="/admin/webhooks?status=failed" icon={<AlertTriangle className="h-5 w-5" />} label="Webhook failures (24h)" value={data.webhook_failed_24h} accent={data.webhook_failed_24h > 0 ? "destructive" : "default"} />
          <Tile to="/admin/plans"   icon={<Tag className="h-5 w-5" />}   label="Active plans"   value={data.plans_active} />
          <Tile to="/admin/add-ons" icon={<Boxes className="h-5 w-5" />} label="Active add-ons" value={data.addons_active} />
          <Tile to="/admin/sync"    icon={<Receipt className="h-5 w-5" />} label="Sync" value="—" hint="Trigger a pass" />
        </div>
      )}
    </div>
  );
}

interface TileProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
  accent?: "default" | "warn" | "destructive";
}

function Tile({ to, icon, label, value, hint, accent = "default" }: TileProps) {
  return (
    <Link to={to} className="block">
      <Card className="transition-shadow hover:shadow-brand-md">
        <CardContent className="flex items-start gap-3 p-4 sm:p-5">
          <div className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            accent === "destructive" ? "bg-destructive/10 text-destructive"
            : accent === "warn"      ? "bg-brand-gold/10 text-brand-gold"
            :                          "bg-brand-accentSoft text-primary"
          )}>{icon}</div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="mt-1 font-display text-2xl font-bold tracking-tight">{value}</p>
            {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function Skeletons() {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <Card key={i}><CardContent className="p-5"><Skeleton className="h-16 w-full" /></CardContent></Card>
      ))}
    </div>
  );
}
